import { Plane } from "lucide-react";
import type { components } from "@cntrl/api-client";
import { formatWhen, journeyProgress, type JourneyProgress } from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

type FlightStatusDto = components["schemas"]["FlightStatusDto"];

/** The leg fields the strip reads — what a crew `TravelItemDto` and a partner's travel row both carry, so the one
 *  strip serves the crew's logistics page and the partner's dashboard (ART-239). */
export interface JourneyLeg {
  travelItemId?: string | null;
  airline?: string | null;
  flightNumber?: string | null;
  departAirport?: string | null;
  arriveAirport?: string | null;
  departLocal?: string | null;
  arriveLocal?: string | null;
  departTz?: string | null;
  arriveTz?: string | null;
}

/** A member's flights stitched into ONE live strip — endpoints, a connection node per layover airport, and a
 * plane positioned across the whole journey by wall clock (parked on a node during a layover). The head shows
 * the flight numbers + the soonest live status (or, with none, `fallbackMeta` — the crew page's assignment count —
 * never a fake "on time"). A single leg is just the two-endpoint case. Positions are client-computed; no vendor
 * position field needed. */
export function JourneyStrip({
  legs,
  statusById,
  fallbackMeta,
  tone = "dark",
}: {
  legs: readonly JourneyLeg[];
  statusById: ReadonlyMap<string, FlightStatusDto>;
  fallbackMeta?: string;
  /** `light` draws it on a light card (the partner's home, ART-239) in the app's own palette — the Day of Show
   *  pages are always dark, which is the default. */
  tone?: "dark" | "light";
}) {
  const n = legs.length;
  const first = legs[0]!;
  const last = legs[n - 1]!;
  const { phase, fraction } = journeyProgress(legs.map((f) => ({ departIso: f.departLocal, arriveIso: f.arriveLocal })));
  const numbers = legs.map((f) => [f.airline, f.flightNumber].filter(Boolean).join(" ")).filter(Boolean).join(" · ") || "Flight";
  // The soonest leg carrying a live status drives the head.
  const statusLeg = legs.find((f) => f.travelItemId && statusById.has(f.travelItemId));
  const status = statusLeg?.travelItemId ? statusById.get(statusLeg.travelItemId) : undefined;
  // Whether we have a schedule to reason from — the only thing that separates a real "Scheduled" from the
  // last-ditch "Unknown". journeyProgress collapses both future and no-times into "pre", so check it here.
  const departKnown = Number.isFinite(new Date(first.departLocal ?? "").getTime());
  const display = status ? flightStatusDisplay(status, phase, departKnown, statusLeg?.departTz) : undefined;
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  return (
    <div className={styles.flightRow} data-testid="logistics-flight" data-phase={phase} data-tone={tone}>
      <div className={styles.flightHead}>
        <span className={styles.logRowTitle} data-selectable>{numbers}</span>
        {display ? (
          <span className={styles.flightStatus} data-status={display.tone}>
            {display.label}
          </span>
        ) : (
          fallbackMeta && <span className={styles.logRowMeta}>{fallbackMeta}</span>
        )}
      </div>
      <div className={styles.flightStrip}>
        <span className={styles.flightEnd}>{first.departAirport || "—"}</span>
        <span className={styles.flightTrack}>
          <span className={styles.flightFill} style={{ width: pct(fraction) }} />
          {/* A node at each connection airport (every leg's arrival but the last). */}
          {legs.slice(0, -1).map((f, i) => (
            <span key={f.travelItemId ?? i} className={styles.flightNode} style={{ left: pct((i + 1) / n) }}>
              <span className={styles.flightNodeCode}>{f.arriveAirport}</span>
            </span>
          ))}
          {/* lucide Plane directly (not the Icon wrapper) so a 13px strip glyph is allowed. */}
          <Plane size={13} className={styles.flightPlane} style={{ left: pct(fraction) }} aria-hidden="true" />
        </span>
        <span className={styles.flightEnd}>{last.arriveAirport || "—"}</span>
      </div>
      <div className={styles.flightTimes}>
        <span>{formatWhen(first.departLocal, first.departTz) || "—"}</span>
        <span>{formatWhen(last.arriveLocal, last.arriveTz) || "—"}</span>
      </div>
    </div>
  );
}

/**
 * The flight-card status label + tone. Our flight-status vendor is post-launch and unconfigured, so the server
 * synthesizes `status: "unknown"` for EVERY flight (and even once a vendor is live it answers "unknown" for a
 * leg it has no reading on yet). So a bare "Unknown" is never surfaced while we can still say where the flight
 * is: the journey PHASE — wall clock over the scheduled times, the same source as the member's phase badge — is
 * the real signal, and the vendor only ever ADDS to it. The actionable vendor states (delayed / cancelled /
 * diverted) override the phase; est/gate ride the pre-departure label. "Unknown" survives ONLY as the
 * last-ditch display: a flight carrying a status row but no schedule to reason from.
 *
 * `tz` = the departure-airport zone, so an estimated departure renders on the SAME clock as the scheduled
 * `departLocal` — otherwise a device-local estimate makes a delay read wrong.
 */
function flightStatusDisplay(
  status: FlightStatusDto,
  phase: JourneyProgress["phase"],
  departKnown: boolean,
  tz?: string | null,
): { label: string; tone: string } {
  const raw = (status.status ?? "").toLowerCase();
  const est = formatWhen(status.estimatedDeparture, tz);
  const gate = status.gate ? ` · Gate ${status.gate}` : "";

  // Things the schedule cannot tell us — these override where the wall clock thinks the flight is.
  if (raw === "cancelled") return { label: "Cancelled", tone: "bad" };
  if (raw === "diverted") return { label: `Diverted${gate}`, tone: "warn" };
  if (raw === "delayed") return { label: `Delayed${est ? ` · Est ${est}` : ""}${gate}`, tone: "warn" };

  // Otherwise the phase is the truth; a vendor "scheduled/active/landed" only ever confirms it.
  switch (phase) {
    case "flying":
      return { label: "In air", tone: "good" };
    case "layover":
      return { label: "Layover", tone: "neutral" };
    case "landed":
      return { label: "Landed", tone: "good" };
    default: // pre-departure
      return departKnown
        ? { label: `Scheduled${est ? ` · Est ${est}` : ""}${gate}`, tone: "neutral" }
        : { label: "Unknown", tone: "neutral" };
  }
}
